import { useState, useRef, useCallback } from 'react'
import {
  LayoutDashboard, Shield, ShieldCheck, Server, Users, Brain,
  Plug, ClipboardList, ChevronLeft, ChevronRight, ChevronDown,
  Hexagon, MessageSquare, BookOpen, GitBranch, BookMarked,
  History, Image, Globe, ScrollText, ListTodo, FolderOpen,
  Activity, Radio, Monitor, Wrench, Zap, Download,
  CreditCard, KeyRound, Crown, UserCog, ClipboardCheck, Code2,
  Package, Layers, Network, FlaskConical,
  Building2, Cloud, Settings, SlidersHorizontal, UserPlus, LogOut,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAppStore } from '@/store'
import { useDashboardNavigation } from '@/hooks/useDashboardNavigation'
import { getToken, clearSession } from '@/lib/auth'
import type { SidebarSection } from '@/types'
import { RemindersWidget } from '@/components/panels/RemindersWidget'

interface NavItem {
  section: SidebarSection
  label: string
  icon: typeof LayoutDashboard
  external?: boolean
  stub?: boolean
  href?: string
  roles?: string[]
}

interface NavGroup {
  title: string
  icon: typeof LayoutDashboard
  items: NavItem[]
  defaultOpen: boolean
}

const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Home',
    icon: LayoutDashboard,
    defaultOpen: true,
    items: [
      { section: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { section: 'platformmodel', label: 'Platform Overview', icon: Network },
    ],
  },
  {
    title: 'Workspace',
    icon: MessageSquare,
    defaultOpen: true,
    items: [
      { section: 'chat', label: 'Chats', icon: MessageSquare },
      { section: 'images', label: 'Images', icon: Image },
      { section: 'library', label: 'Library', icon: FolderOpen },
      { section: 'sessions', label: 'Sessions', icon: History },
      { section: 'tasks', label: 'Tasks', icon: ListTodo },
    ],
  },
  {
    title: 'Development',
    icon: Code2,
    defaultOpen: true,
    items: [
      { section: 'architecture', label: 'Architecture', icon: Building2 },
      { section: 'sandbox', label: 'Sandbox', icon: FlaskConical },
      { section: 'buildreviews', label: 'Build Reviews', icon: Shield },
      { section: 'deployapprovals', label: 'Deployments', icon: ClipboardCheck },
      { section: 'pipeline', label: 'Automation', icon: Zap },
      { section: 'playbooks', label: 'Playbooks', icon: BookMarked },
    ],
  },
  {
    title: 'Intelligence',
    icon: Brain,
    defaultOpen: true,
    items: [
      { section: 'knowledge', label: 'Knowledge', icon: BookOpen },
      { section: 'memory', label: 'Memory', icon: Brain },
      { section: 'drift', label: 'Drift', icon: GitBranch },
      { section: 'skillsregistry', label: 'Skills & Models', icon: Package },
      { section: 'mcp', label: 'MCP', icon: Plug },
    ],
  },
  {
    title: 'Platform',
    icon: Monitor,
    defaultOpen: true,
    items: [
      { section: 'opsbridgefeatures', label: 'OPS Features', icon: Layers },
      { section: 'downloads', label: 'Download Center', icon: Download },
      { section: 'extensionactivities', label: 'Extension Activities', icon: Activity },
      { section: 'toolsregistry', label: 'Platform Feature Registry', icon: Wrench },
      { section: 'workers', label: 'Workers', icon: Server },
      { section: 'browserlive', label: 'Browser Live View', icon: Radio },
    ],
  },
  {
    title: 'Modules',
    icon: Plug,
    defaultOpen: true,
    items: [
      { section: 'wordpress', label: 'WordPress', icon: Globe, external: true },
      { section: 'cloudflare', label: 'Cloudflare', icon: Cloud },
      { section: 'github', label: 'GitHub', icon: Code2 },
    ],
  },
  {
    title: 'Governance',
    icon: Shield,
    defaultOpen: true,
    items: [
      { section: 'security', label: 'Security', icon: ShieldCheck },
      { section: 'governance', label: 'Governance', icon: Shield },
      { section: 'capabilitycontrol', label: 'Capability Control', icon: ShieldCheck },
      { section: 'auditcentre', label: 'Audit Centre', icon: ClipboardList },
      { section: 'actionlog', label: 'Action Log', icon: ScrollText },
    ],
  },
  {
    title: 'Tenant Admin',
    icon: Crown,
    defaultOpen: false,
    items: [
      { section: 'tenants', label: 'Tenants', icon: Users },
      { section: 'tenantadmin', label: 'Licensing', icon: Crown },
      { section: 'billing', label: 'Billing', icon: CreditCard },
      { section: 'activity', label: 'Tenant Activity', icon: Activity },
      { section: 'routes', label: 'Route Registry', icon: Radio },
    ],
  },
  {
    title: 'Staff',
    icon: UserCog,
    defaultOpen: false,
    items: [
      { section: 'team', label: 'Staff Portal', icon: UserCog },
    ],
  },
  {
    title: 'Account',
    icon: Settings,
    defaultOpen: false,
    items: [
      { section: 'myprofile', label: 'My Profile', icon: UserPlus },
      { section: 'appearance', label: 'Preferences', icon: SlidersHorizontal },
    ],
  },
]

interface SidebarProps {
  mobileDrawer?: boolean;
  onNavClick?: () => void
}

export function Sidebar({ onNavClick, mobileDrawer = false }: SidebarProps) {
  const { sidebarCollapsed, toggleSidebar, activeSection, setActiveSection } = useAppStore()
  const navigation = useDashboardNavigation()
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {}
    NAV_GROUPS.forEach((g) => { initial[g.title] = g.defaultOpen })
    return initial
  })

  // --- Edge-swipe drag gesture, additive to the toggle button below ---
  // Width targets, roughly matching the reference (Claude's own app side
  // panel): collapsed stays icon-only, expanded is viewport-relative so it
  // scales sensibly across phone sizes, capped so it doesn't get silly on
  // a tablet or desktop. Binary open/closed on release, not a freely
  // resizable width -- matches how the referenced panel actually behaves.
  const COLLAPSED_PX = 64
  const EXPANDED_VW = 0.72
  const EXPANDED_CAP_PX = 320
  const COMMIT_FRACTION = 0.35

  const [dragX, setDragX] = useState<number | null>(null)
  const touchStart = useRef({ x: 0, y: 0 })
  const gestureDir = useRef<'horizontal' | 'vertical' | null>(null)

  const expandedPx = useCallback(() => {
    if (typeof window === 'undefined') return EXPANDED_CAP_PX
    return Math.min(window.innerWidth * EXPANDED_VW, EXPANDED_CAP_PX)
  }, [])

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
    gestureDir.current = null
  }, [])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const dx = e.touches[0].clientX - touchStart.current.x
    const dy = e.touches[0].clientY - touchStart.current.y
    if (gestureDir.current === null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return
      gestureDir.current = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical'
    }
    if (gestureDir.current !== 'horizontal') return
    // Only actionable in the direction that makes sense for the current state --
    // dragging right to open when collapsed, left to close when expanded.
    if ((sidebarCollapsed && dx < 0) || (!sidebarCollapsed && dx > 0)) return
    e.preventDefault()
    setDragX(dx)
  }, [sidebarCollapsed])

  const handleTouchEnd = useCallback(() => {
    if (gestureDir.current === 'horizontal' && dragX !== null) {
      const range = expandedPx() - COLLAPSED_PX
      const progress = Math.abs(dragX) / range
      if (progress > COMMIT_FRACTION) toggleSidebar()
    }
    setDragX(null)
    gestureDir.current = null
  }, [dragX, expandedPx, toggleSidebar])

  const liveWidthPx = (() => {
    if (dragX === null) return null
    const base = sidebarCollapsed ? COLLAPSED_PX : expandedPx()
    const raw = base + dragX
    return Math.max(COLLAPSED_PX, Math.min(expandedPx(), raw))
  })()

  const handleKeystoreDownload = async () => {
    const token = getToken()
    const res = await fetch('/api/dashboard/download/keystore', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!res.ok) { alert('Download failed — are you logged in as Super Admin?'); return }
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'opsbridge.keystore'
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleLogout = () => {
    clearSession()
    sessionStorage.removeItem('ops_pending_session')
    window.location.href = 'https://admin.opsbakersguild.com/logout'
  }

  const handleNav = (item: NavItem) => {
    if (item.href) {
      window.open(item.href, '_blank', 'noopener,noreferrer')
      return
    }
    setActiveSection(item.section)
    onNavClick?.()
  }

  const toggleGroup = (title: string) => {
    setOpenGroups((prev) => ({ ...prev, [title]: !prev[title] }))
  }

  const groupHasActive = (group: NavGroup) =>
    group.items.some((item) => !item.href && item.section === activeSection)

  const canSeeItem = (item: NavItem) => {
    return navigation.canSee(item.section)
  }

  return (
    <aside
      onTouchStart={mobileDrawer ? undefined : handleTouchStart}
      onTouchMove={mobileDrawer ? undefined : handleTouchMove}
      onTouchEnd={mobileDrawer ? undefined : handleTouchEnd}
      style={liveWidthPx !== null ? { width: `${liveWidthPx}px`, transition: 'none' } : undefined}
      className={cn(
        'ops-sidebar flex flex-col h-full text-sidebar-text transition-all duration-300 ease-out',
        sidebarCollapsed ? 'w-16' : 'w-[min(72vw,320px)]'
      )}
    >
      <div className="ops-sidebar-brand flex items-center gap-4 px-5 shrink-0">
        <div className="ops-brand-mark"><Hexagon className="h-8 w-8" strokeWidth={2.5} /></div>
        {!sidebarCollapsed && (
          <div className="overflow-hidden leading-tight">
            <div className="text-[22px] font-bold text-sidebar-text-active whitespace-nowrap">OPS <span className="font-normal text-[17px]">by BakersGuild</span></div>
            <div className="text-[14px] text-sidebar-text tracking-wide mt-1">Command Centre</div>
          </div>
        )}
      </div>

      <nav className="flex-1 py-4 px-4 overflow-y-auto sidebar-scrollbar">
        {navigation.canSee('commandcentre') && (
          <button
            onClick={() => handleNav({ section: 'commandcentre', label: 'Command Centre', icon: Activity })}
            aria-label={sidebarCollapsed ? 'Command Centre' : undefined}
            className={cn(
              'ops-command-button flex items-center gap-3 w-full px-3 py-3 text-sm transition-colors duration-100 cursor-pointer mb-3',
              activeSection === 'commandcentre'
                ? 'bg-accent/10 text-slate-950 dark:text-sidebar-text-active shadow-[inset_2px_0_0_0_hsl(var(--accent))]'
                : 'text-slate-800 hover:bg-slate-950/[0.05] hover:text-black dark:text-sidebar-text dark:hover:bg-sidebar-hover dark:hover:text-sidebar-text-active'
            )}
          >
            <span className="ops-icon-tile"><Activity className={cn('h-5 w-5 shrink-0', activeSection === 'commandcentre' && 'text-accent')} /></span>
            {!sidebarCollapsed && <span className="font-semibold text-[15px]">Command Centre</span>}
          </button>
        )}

        {sidebarCollapsed && navigation.canSee('commandcentre') && <div className="border-t border-white/5 dark:border-black/5 my-1" />}

        {NAV_GROUPS.map((group) => {
          const isOpen = openGroups[group.title] ?? group.defaultOpen
          const hasActive = groupHasActive(group)
          const GroupIcon = group.icon
          const visibleItems = group.items.filter(canSeeItem)
          if (visibleItems.length === 0) return null

          if (sidebarCollapsed) {
            return visibleItems.map((item) => (
              <button
                key={item.section}
                onClick={() => !item.stub && handleNav(item)}
                aria-label={item.label}
                className={cn(
                  'flex items-center justify-center w-full rounded-lg p-2.5 transition-colors duration-100 cursor-pointer',
                  item.stub && 'opacity-30 cursor-not-allowed',
                  !item.href && activeSection === item.section
                    ? 'ops-nav-item-active bg-sidebar-active/10 text-sidebar-text-active'
                    : 'text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-active'
                )}
              >
                <item.icon className={cn('h-4.5 w-4.5', !item.href && activeSection === item.section && 'text-sidebar-active')} />
              </button>
            ))
          }

          return (
            <div key={group.title} className="ops-nav-group mt-1">
              <button
                onClick={() => toggleGroup(group.title)}
                className={cn(
                  'ops-group-heading flex items-center gap-3 w-full min-h-[48px] px-2 py-2 text-[12px] font-bold uppercase tracking-[0.12em] transition-colors duration-100 cursor-pointer',
                  hasActive ? 'text-slate-950 dark:text-sidebar-text-active' : 'text-slate-900 hover:text-black dark:text-sidebar-text-active/90 dark:hover:text-sidebar-text-active'
                )}
              >
                <span className="ops-icon-tile ops-icon-tile-group"><GroupIcon className="h-5 w-5" /></span>
                <span className="flex-1 text-left">{group.title}</span>
                <ChevronDown className={cn('h-3 w-3 transition-transform duration-200', !isOpen && '-rotate-90')} />
              </button>

              {isOpen && (
                <div className="space-y-0.5 mt-0.5">
                  {visibleItems.map((item) => (
                    <button
                      key={item.section}
                      onClick={() => !item.stub && handleNav(item)}
                      className={cn(
                        'ops-nav-item flex items-center gap-3 w-full rounded-xl px-3 py-2 text-sm transition-colors duration-100',
                        item.stub && 'opacity-30 cursor-not-allowed',
                        !item.stub && 'cursor-pointer',
                        !item.href && activeSection === item.section
                          ? 'ops-nav-item-active bg-accent/10 text-slate-950 dark:text-sidebar-text-active'
                          : !item.stub && 'text-slate-800 hover:bg-slate-950/[0.05] hover:text-black dark:text-sidebar-text dark:hover:bg-sidebar-hover dark:hover:text-sidebar-text-active'
                      )}
                    >
                      <span className="ops-icon-tile ops-icon-tile-item"><item.icon className={cn('h-4 w-4 shrink-0', !item.href && activeSection === item.section && 'text-sidebar-active')} /></span>
                      <span className="flex-1 text-left truncate">{item.label}</span>
                      {item.external && (
                        <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/10 text-amber-400/70 font-medium shrink-0">EXT</span>
                      )}
                      {item.href && !item.external && (
                        <span className="text-[9px] px-1 py-0.5 rounded bg-accent/10 text-accent/70 font-medium shrink-0">↗</span>
                      )}
                      {item.stub && (
                        <span className="text-[9px] px-1 py-0.5 rounded bg-white/5 dark:bg-black/5 text-text-muted font-medium shrink-0">P2</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )
        })}

        {!sidebarCollapsed && <RemindersWidget />}

        <div className="ops-sidebar-actions mt-5 pt-3 space-y-1">
        <a
          href="/downloads/opsbridge.apk"
          download="opsbridge.apk"
          aria-label="Download app"
          className={cn(
            'flex items-center gap-3 w-full rounded-lg px-3 py-2 text-sm text-slate-900 hover:bg-slate-950/[0.05] hover:text-black dark:text-sidebar-text dark:hover:bg-sidebar-hover dark:hover:text-sidebar-text-active transition-colors duration-100',
            sidebarCollapsed ? 'justify-center' : ''
          )}
        >
          <span className="ops-icon-tile ops-icon-tile-item"><Download className="h-4 w-4 shrink-0" /></span>
          {!sidebarCollapsed && (
            <>
              <span className="flex-1 text-left">Download app</span>
              <span className="text-[9px] px-1 py-0.5 rounded bg-green-500/10 text-green-400/70 font-medium shrink-0">APK</span>
            </>
          )}
        </a>
        {navigation.super_admin && (
          <button
            onClick={handleKeystoreDownload}
            aria-label="Download keystore"
            className={cn(
              'flex items-center gap-3 w-full rounded-lg px-3 py-2 text-sm text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-active transition-colors duration-100 cursor-pointer',
              sidebarCollapsed ? 'justify-center' : ''
            )}
          >
            <span className="ops-icon-tile ops-icon-tile-item"><KeyRound className="h-4 w-4 shrink-0" /></span>
            {!sidebarCollapsed && (
              <>
                <span className="flex-1 text-left">Download keystore</span>
                <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/10 text-amber-400/70 font-medium shrink-0">SA</span>
              </>
            )}
          </button>
        )}
        <button
          onClick={handleLogout}
          aria-label="Log out"
          className={cn(
            'flex items-center gap-3 w-full rounded-lg px-3 py-2 text-sm text-slate-900 hover:bg-red-500/10 hover:text-red-700 dark:text-sidebar-text dark:hover:text-red-300 transition-colors duration-100 cursor-pointer',
            sidebarCollapsed ? 'justify-center' : ''
          )}
        >
          <span className="ops-icon-tile ops-icon-tile-item"><LogOut className="h-4 w-4 shrink-0" /></span>
          {!sidebarCollapsed && <span className="flex-1 text-left">Log out</span>}
        </button>
        </div>
      </nav>

      <div className="ops-collapse-area p-2">
        <button
          onClick={toggleSidebar}
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="flex items-center justify-center w-full rounded-lg p-2 text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-active transition-colors duration-100 cursor-pointer"
        >
          {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </div>
      {!sidebarCollapsed && (
        <div className="ops-sidebar-signoff">
          <Hexagon className="h-4 w-4 text-accent" strokeWidth={2.5} />
          <span>OPS by BakersGuild</span><i />
          <span>Smarter Tools. Stronger Websites.</span>
        </div>
      )}
    </aside>
  )
}
